import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Users, Package, IndianRupee, Wallet, Percent, TrendingUp, ArrowRight, Activity } from "lucide-react";
import { appApi, inr } from "@/lib/api";

export default function AdminOverview() {
  const [stats, setStats] = useState(null);
  const [board, setBoard] = useState(null);

  useEffect(() => {
    appApi.get("admin/stats").then(setStats).catch(() => setStats({ error: true }));
    Promise.allSettled([appApi.get("admin/orders/amazon-b2c"), appApi.get("admin/orders/b2b")]).then(([a, b]) => {
      const all = [
        ...(a.status === "fulfilled" ? a.value.orders || [] : []),
        ...(b.status === "fulfilled" ? b.value.orders || [] : []),
      ];
      const counts = {};
      for (const o of all) {
        const s = String(o.status || "unknown").toLowerCase();
        counts[s] = (counts[s] || 0) + 1;
      }
      setBoard(counts);
    });
  }, []);

  if (!stats)
    return <p className="font-mono text-xs uppercase tracking-[0.3em] text-zinc-400" data-testid="admin-overview-loading">Loading platform stats…</p>;
  if (stats.error)
    return <p className="text-sm font-semibold text-red-600" data-testid="admin-overview-error">Could not load stats.</p>;

  const cards = [
    { label: "Merchants", value: stats.users_total, icon: Users, testid: "stat-merchants", money: false },
    { label: "Orders (all time)", value: stats.orders_total, icon: Package, testid: "stat-orders-total", money: false },
    { label: "Orders (7d)", value: stats.orders_week, icon: TrendingUp, testid: "stat-orders-week", money: false },
    { label: "Orders (30d)", value: stats.orders_month, icon: Package, testid: "stat-orders-month", money: false },
    { label: "GMV", value: stats.gmv_inr, icon: IndianRupee, testid: "stat-gmv", money: true },
    { label: "Revenue (markup)", value: stats.revenue_inr, icon: IndianRupee, testid: "stat-revenue", money: true },
    { label: "Wallet Float", value: stats.wallet_float_inr, icon: Wallet, testid: "stat-wallet-float", money: true },
    { label: "Global Markup", value: `${stats.markup_pct}%`, icon: Percent, testid: "stat-markup", money: false, raw: true },
  ];

  return (
    <div data-testid="admin-overview-page" className="space-y-6">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <p className="text-xs font-mono font-bold uppercase tracking-[0.2em] text-blaze">Admin</p>
          <h1 className="font-display font-black tracking-tight text-3xl sm:text-4xl mt-1">Platform Command</h1>
        </div>
        <Link to="/admin/merchants" data-testid="admin-overview-merchants-cta"
          className="inline-flex items-center gap-2 bg-acid text-white text-sm font-bold px-5 py-3 border-2 border-obsidian shadow-[4px_4px_0px_0px_#0A0A0A] hover:shadow-none hover:translate-x-[4px] hover:translate-y-[4px] transition-all duration-150">
          All Merchants <ArrowRight className="w-4 h-4" />
        </Link>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {cards.map((c) => (
          <div key={c.testid} data-testid={c.testid} className="border-2 border-obsidian bg-white p-5 hover:shadow-[6px_6px_0px_0px_#0A0A0A] hover:-translate-x-0.5 hover:-translate-y-0.5 transition-all duration-150">
            <div className="flex items-center justify-between">
              <p className="text-[11px] font-mono font-bold uppercase tracking-widest text-zinc-500">{c.label}</p>
              <c.icon className="w-4 h-4 text-zinc-400" />
            </div>
            <p className="font-display font-black text-3xl tracking-tight mt-2">
              {c.raw ? c.value : c.money ? inr(c.value) : Number(c.value || 0).toLocaleString("en-IN")}
            </p>
          </div>
        ))}
      </div>

      {board && (
        <div className="border-2 border-obsidian bg-white" data-testid="live-status-board">
          <div className="px-5 py-4 border-b-2 border-obsidian flex items-center justify-between">
            <p className="text-[11px] font-mono font-bold uppercase tracking-widest text-zinc-500 flex items-center gap-2">
              <Activity className="w-4 h-4 text-blaze" /> Live shipment board
            </p>
            <Link to="/admin/orders" data-testid="board-view-all" className="text-xs font-bold text-blaze hover:underline">All orders</Link>
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-4">
            {[
              { k: "in-transit", l: "In Transit" },
              { k: "out-for-delivery", l: "Out for Delivery" },
              { k: "ndr", l: "NDR" },
              { k: "delivered", l: "Delivered" },
            ].map((s, i) => (
              <div key={s.k} data-testid={`board-${s.k}`} className={`p-5 ${i !== 0 ? "border-l-2" : ""} border-obsidian`}>
                <p className="text-[10px] font-mono font-bold uppercase tracking-widest text-zinc-500">{s.l}</p>
                <p className="font-display font-black text-3xl tracking-tight mt-1">{board[s.k] || 0}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="border-2 border-obsidian bg-obsidian text-white p-6 flex flex-wrap items-center justify-between gap-4">
        <p className="text-sm text-zinc-400 max-w-xl">
          Live numbers from the ShipZilla app backend — every merchant, order and rupee here is real and in sync with the mobile app.
        </p>
        <span className="font-mono text-xs font-bold uppercase tracking-widest text-sky-300" data-testid="admin-live-indicator">● Live sync</span>
      </div>
    </div>
  );
}
